import React, { useState, useEffect, useCallback, useRef } from "react";
import { customFetch } from '../apiClient';
import QuillEditor from "../components/QuillEditor";

export default function UsgMasterPage() {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  
  const [testName, setTestName] = useState("");
  const [testContent, setTestContent] = useState("");
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const modalRef = useRef(null);



  // Auto-dismiss alerts
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 4000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
    const r = await customFetch(
        `/UsgMaster?search=${encodeURIComponent(search)}&activeOnly=true`
      );
      if (r.ok) setTests(await r.json());
    } catch { 
      setError("Failed to load templates.");
    } finally { 
      setLoading(false); 
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  // ── Keyboard Navigation inside Modal ──
  const handleKeyDown = useCallback((e) => {
    if (e.key !== 'Enter' || !formOpen) return;
    
    // 🔥 If the user is typing inside the Quill editor, let Enter make a new line!
    if (e.target.closest('.ql-editor')) return;

    const focusableSelector = 'input:not([disabled]):not([type="hidden"]), button:not([disabled])';
    const focusableElements = Array.from(modalRef.current?.querySelectorAll(focusableSelector) || []);
    
    if (focusableElements.length === 0) return;
    const currentIndex = focusableElements.indexOf(e.target);

    if (currentIndex >= 0 && currentIndex < focusableElements.length - 1) {
      e.preventDefault();
      focusableElements[currentIndex + 1]?.focus();
    }
  }, [formOpen]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  function openCreate() {
    setEditItem(null);
    setTestName("");
    setTestContent("");
    setError("");
    setFormOpen(true);
  }

  function openEdit(item) {
    setEditItem(item);
    setTestName(item.testName);
    setTestContent(item.testContent || "");
    setError("");
    setFormOpen(true);
  }

  function closeForm() {
    setFormOpen(false);
    setEditItem(null);
    setTestName("");
    setTestContent("");
  }

  async function handleSave() {
    if (!testName.trim()) {
      setError("Test name is required");
      return;
    }
    setSaving(true);
    setError("");
    
    try {
    const endpoint = editItem ? `/UsgMaster/${editItem.id}` : `/UsgMaster`;
      const method = editItem ? "PUT" : "POST";
      const body = editItem ? { id: editItem.id, testName, testContent } : { testName, testContent };

      // ✅ 5. customFetch lagaya aur headers property options se nikal di
      const r = await customFetch(endpoint, { 
        method, 
        body: JSON.stringify(body) 
      });


      if (r.ok || r.status === 204 || r.status === 201) {
        setSuccess(`Template ${editItem ? "updated" : "saved"} successfully!`);
        closeForm();
        load();
      } else {
        setError("Failed to save template");
      }
    } catch {
      setError("Network error occurred while saving.");
    } finally { 
      setSaving(false); 
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("Are you sure you want to delete this test template?")) return;
    setLoading(true);
    try {
      const res = await customFetch(`/UsgMaster/${id}`, { method: "DELETE" });
      if (res.ok) {
        setSuccess("Template deleted successfully!");
        load();
      } else {
        setError("Failed to delete template.");
      }
    } catch {
      setError("Network error occurred.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="advance-form-container compact-ui">
      <div className="advance-card">
        
        {/* Header */}
        <div className="advance-header">
          <div className="header-left">
            <div className="header-icon">
              <i className="bi bi-journal-medical"></i>
            </div>
            <div className="header-text">
              <h5>USG & Test Master</h5>
              <p>Create and manage pre-formatted report templates</p>
            </div>
          </div>
          <button type="button" className="btn-view-list" onClick={openCreate} title="New Template">
            <i className="bi bi-plus-circle"></i> New Template
          </button>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert-custom alert-danger slide-in">
            <i className="bi bi-exclamation-triangle-fill"></i>
            <span>{error}</span>
            <button className="alert-close" onClick={() => setError("")}><i className="bi bi-x"></i></button>
          </div>
        )}
        {success && (
          <div className="alert-custom alert-success slide-in">
            <i className="bi bi-check-circle-fill"></i>
            <span>{success}</span>
            <button className="alert-close" onClick={() => setSuccess("")}><i className="bi bi-x"></i></button>
          </div>
        )}

        <div className="main-content">
          
          {/* Top Panel: Search */}
          <div className="form-panel">
            <div className="form-section">
              <div className="section-title">
                <i className="bi bi-search"></i>
                <span>Search Templates</span>
              </div>
              <div className="section-content">
                <div className="form-row search-date-row">
                  <div className="form-group">
                    <input
                      className="form-control-custom"
                      placeholder="Type test name to search..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Panel: Existing Table */}
          <div className="existing-records-panel">
            <div className="existing-records-section">
              <div className="section-title">
                <i className="bi bi-list-ul"></i>
                <span>Template Library</span>
                <span className="badge-count-small">{tests.length}</span>
              </div>
              <div className="existing-records-content" style={{ padding: 0 }}>
                {loading && tests.length === 0 ? (
                   <div className="empty-state-small">
                     <div className="spinner-border text-primary" role="status" style={{width: '20px', height: '20px'}}></div>
                     <p>Loading templates...</p>
                   </div>
                ) : tests.length === 0 ? (
                  <div className="empty-state-small">
                    <i className="bi bi-inbox"></i>
                    <p>No templates found. Click 'New Template' to get started.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="records-table">
                      <thead>
                        <tr>
                          <th style={{ width: '60px' }} className="text-center">Sr No</th>
                          <th style={{ width: '250px' }}>Test Name</th>
                          <th>Content Preview</th>
                          <th style={{ width: '120px' }}>Created By</th>
                          <th style={{ width: '90px' }} className="text-center">
                            <i className="bi bi-tools"></i> Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {tests.map((t) => (
                          <tr key={t.id}>
                            <td className="text-center">
                              <span className="amount-badge" style={{background: '#e0f2fe', color: '#0284c7'}}>
                                {t.hid_SrNo}
                              </span>
                            </td>
                            <td><strong>{t.testName}</strong></td>
                            <td>
                              <div className="remarks-text" style={{ 
                                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' 
                              }} dangerouslySetInnerHTML={{ __html: t.testContent || "<em>No content</em>" }} />
                            </td>
                            <td><span className="text-muted"><i className="bi bi-person me-1"></i>{t.created_By}</span></td>
                            <td className="text-center">
                              <div className="action-buttons">
                                <button className="btn-icon btn-edit" onClick={() => openEdit(t)} title="Edit">
                                  <i className="bi bi-pencil-square"></i>
                                </button>
                                <button className="btn-icon btn-delete" onClick={() => handleDelete(t.id)} title="Delete">
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Edit/Create Modal */}
      {formOpen && (
        <div className="modal-overlay">
          <div className="modal-content slide-in" style={{ maxWidth: '850px' }} ref={modalRef}>
            <div className="modal-header">
              <h5><i className="bi bi-file-earmark-text"></i> {editItem ? "Edit Template" : "New Template"}</h5>
              <button className="btn-modal-close" onClick={closeForm}><i className="bi bi-x"></i></button>
            </div>
            
            <div className="modal-body">
              <div className="form-group mb-3">
                <label>Test / Report Name <span className="required">*</span></label>
                <input 
                  type="text" 
                  className="form-control-custom" 
                  placeholder="e.g. USG Whole Abdomen" 
                  value={testName} 
                  onChange={(e) => setTestName(e.target.value)} 
                  autoFocus
                />
              </div>

              <div className="form-group mb-1">
                <label>Report Format Template</label>
              </div>
              
              {/* Quill Editor styling blends seamlessly with the compact theme */}
              <div style={{ border: '1px solid #cbd5e0', borderRadius: '6px', overflow: 'hidden' }}>
                <QuillEditor
                  value={testContent}
                  onChange={setTestContent}
                  minHeight="350px"
                />
              </div>
            </div>

            <div className="modal-footer form-actions">
              <button className="btn-secondary" onClick={closeForm}>Cancel</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : <><i className="bi bi-check-circle"></i> Save Template</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS: SCALED DOWN COMPACT UI (Same as IpdAdvanceForm) */}
      <style>{`
        .compact-ui {
          font-family: system-ui, -apple-system, sans-serif;
        }

        .advance-form-container {
          padding: 0;
          max-height: 100vh;
          overflow-y: auto;
          background: #f8fafc;
          min-height: 100vh;
          padding-bottom: 40px;
        }

        .advance-card {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
          border: 1px solid #e0e6ed;
          overflow: visible;
        }

        .main-content {
          display: flex;
          flex-direction: column;
          gap: 12px;
          padding: 12px 16px;
        }
        
        .form-panel, .existing-records-panel { width: 100%; }

        .form-section, .existing-records-section {
          background: #ffffff; 
          border: 1px solid #e0e6ed; 
          border-radius: 8px; 
          overflow: visible;
        }

        .section-title {
          background: #f0f4f8; padding: 8px 12px; display: flex; align-items: center; gap: 6px;
          border-bottom: 2px solid var(--theme-color, #0088cc);
          border-top-left-radius: 8px; 
          border-top-right-radius: 8px; 
        }
        .section-title i { font-size: 13px; color: var(--theme-color, #0088cc); }
        .section-title span { font-size: 12px; font-weight: 600; color: #2c3e50; flex: 1; }

        .section-content { padding: 12px; background: white; border-bottom-left-radius: 8px; border-bottom-right-radius: 8px;}
        
        .badge-count-small {
          background: white; color: var(--theme-color, #0088cc);
          padding: 2px 6px; border-radius: 10px; font-size: 10px; font-weight: bold;
        }

        .empty-state-small { text-align: center; padding: 2.5rem 1rem; color: #9ca3af; }
        .empty-state-small i { font-size: 2.5rem; margin-bottom: 0.3rem; display: block; }
        
        /* Table Styles (Scaled Down) */
        .table-responsive { overflow-x: auto; border-bottom-left-radius: 8px; border-bottom-right-radius: 8px;}
        .records-table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 11px; }
        .records-table thead tr { background: var(--theme-color, #0088cc); }
        .records-table thead th {
          padding: 8px 10px; text-align: left; font-weight: 600; color: white;
          font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.5px;
          white-space: nowrap; border: none;
        }
        .records-table tbody tr { background: white; transition: all 0.2s; }
        .records-table tbody tr:hover { background: #f8f9fa; transform: translateY(-1px); box-shadow: 0 2px 5px rgba(0,0,0,0.05); }
        .records-table tbody td { padding: 8px 10px; border-bottom: 1px solid #e9ecef; vertical-align: middle; }

        .text-center { text-align: center; }

        /* Badges & Text Styling */
        .amount-badge { display: inline-flex; align-items: center; gap: 0.2rem; background: #dcfce7; color: #166534; padding: 0.2rem 0.6rem; border-radius: 4px; font-weight: 700; font-size: 11px; }
        .remarks-text { color: #4b5563; font-size: 11px; font-style: italic; }
        .text-muted { color: #9ca3af; font-size: 11px; }

        /* Action Buttons */
        .action-buttons { display: flex; gap: 0.4rem; justify-content: center; }
        .btn-icon { width: 26px; height: 26px; border: none; border-radius: 4px; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; font-size: 12px; }
        .btn-edit { background: #dbeafe; color: #1e40af; }
        .btn-edit:hover { background: #1e40af; color: white; transform: translateY(-1px); }
        .btn-delete { background: #fee2e2; color: #dc2626; }
        .btn-delete:hover { background: #dc2626; color: white; transform: translateY(-1px); }
        
        /* Form Components */
        .form-row { display: grid; gap: 10px; }
        .form-row.search-date-row { grid-template-columns: 1fr; }
        .form-group { display: flex; flex-direction: column; }
        .form-group label { font-size: 11px; font-weight: 600; color: #2c3e50; margin-bottom: 4px; display: flex; align-items: center; gap: 4px; }
        .required { color: #e74c3c; }

        .form-control-custom { width: 100%; padding: 6px 10px; border: 1.5px solid #cbd5e0; border-radius: 6px; font-size: 11px; transition: all 0.2s; outline: none; }
        .form-control-custom:focus { border-color: var(--theme-color, #0088cc); box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05); }
        .form-control-custom:disabled { background: #f3f4f6; opacity: 0.6; cursor: not-allowed; }

        .form-actions { display: flex; justify-content: flex-end; gap: 8px; }
        .btn-primary, .btn-secondary { padding: 6px 16px; border-radius: 6px; font-size: 11px; font-weight: 600; border: none; cursor: pointer; transition: all 0.2s ease; display: flex; align-items: center; gap: 5px; }
        .btn-primary { background: var(--theme-color, #0088cc); color: white; }
        .btn-primary:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
        .btn-secondary { background: #e0e6ed; color: #2c3e50; }
        .btn-secondary:hover:not(:disabled) { background: #cbd5e0; transform: translateY(-1px); }

        /* MODAL CSS */
        .modal-overlay {
          position: fixed; top: 0; left: 0; right: 0; bottom: 0;
          background: rgba(0, 0, 0, 0.4); z-index: 10000;
          display: flex; align-items: center; justify-content: center;
          backdrop-filter: blur(2px);
          padding: 20px;
        }
        .modal-content {
          background: white; border-radius: 8px; width: 100%; max-height: 95vh;
          box-shadow: 0 4px 20px rgba(0,0,0,0.15); display: flex; flex-direction: column;
        }
        .modal-header {
          padding: 12px 16px; border-bottom: 1px solid #e0e6ed;
          display: flex; justify-content: space-between; align-items: center;
          background: #f8f9fa; border-top-left-radius: 8px; border-top-right-radius: 8px;
        }
        .modal-header h5 { margin: 0; font-size: 14px; font-weight: 600; color: #2c3e50; display: flex; align-items: center; gap: 6px; }
        .modal-header h5 i { color: var(--theme-color, #0088cc); }
        .btn-modal-close { background: none; border: none; font-size: 20px; color: #6b7280; cursor: pointer; padding: 0; display: flex; }
        .btn-modal-close:hover { color: #e74c3c; }
        .modal-body { padding: 16px; overflow-y: auto; }
        .modal-footer {
          padding: 12px 16px; border-top: 1px solid #e0e6ed;
          background: #f8f9fa; border-bottom-left-radius: 8px; border-bottom-right-radius: 8px;
          margin-top: 0;
        }

        /* Animations */
        @keyframes slideIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }
        .slide-in { animation: slideIn 0.2s ease; }

        /* Headers and Alerts */
        .advance-header {
          background: var(--theme-color, #0088cc);
          padding: 10px 16px; display: flex; justify-content: space-between;
          align-items: center; border-bottom: 2px solid #00d4aa;
          border-top-left-radius: 8px; border-top-right-radius: 8px;
        }
        .header-left { display: flex; align-items: center; gap: 10px; }
        .header-icon {
          width: 34px; height: 34px; background: rgba(255, 255, 255, 0.2);
          border-radius: 6px; display: flex; align-items: center; justify-content: center;
          font-size: 16px; color: white;
        }
        .header-text h5 { margin: 0; color: white; font-size: 15px; font-weight: 600; line-height: 1.1; }
        .header-text p { margin: 0; color: rgba(255, 255, 255, 0.85); font-size: 10.5px; }

        .btn-view-list {
          padding: 6px 12px; border-radius: 6px; border: 1.5px solid rgba(255, 255, 255, 0.3);
          background: rgba(255, 255, 255, 0.15); color: white; font-size: 11px; font-weight: 600;
          cursor: pointer; transition: all 0.2s ease; display: flex; align-items: center; gap: 5px;
        }
        .btn-view-list:hover { background: white; color: var(--theme-color, #0088cc); transform: translateY(-1px); }

        .alert-custom { margin: 12px 16px 0; padding: 10px 14px; border-radius: 6px; display: flex; align-items: center; gap: 8px; font-size: 11px; font-weight: 600; position: relative; }
        .alert-danger { background: #fee2e2; color: #b91c1c; border: 1px solid #fecaca; }
        .alert-success { background: #dcfce7; color: #15803d; border: 1px solid #bbf7d0; }
        .alert-close { position: absolute; right: 8px; background: transparent; border: none; cursor: pointer; opacity: 0.6; }
      `}</style>
    </div>
  );
}